import React, { useState, useEffect } from "react";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";

export default function PaginatedAutocomplete({
  options,
  label,
  value,
  onChange,
  error,
  helperText,
  getOptionLabel = (op) => op,
  required = true,
  onFocus,
  isLoading,
  freeSolo = false,
  isOptionEqualToValue,
  ...rest
}) {
  const [inputValue, setInputValue] = useState("");
  const [displayedOptions, setDisplayedOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Show the first 100 elements initially for better UX
    setDisplayedOptions(options.slice(0, 100));
  }, [options]);

  useEffect(() => {
    if (inputValue) {
      setLoading(true);
      // Filter options based on the input value - supports partial matching
      const filteredOptions = options.filter((option) => {
        const optionLabel = getOptionLabel(option).toLowerCase();
        const searchValue = inputValue.toLowerCase();
        // Check if any word in the label contains the search value
        return optionLabel.includes(searchValue);
      });
      // Show up to the first 100 filtered elements
      setDisplayedOptions(filteredOptions.slice(0, 100));
      setLoading(false);
    } else {
      // Reset to the first 100 elements when input is cleared
      setDisplayedOptions(options.slice(0, 100));
    }
  }, [inputValue, options, getOptionLabel]);

  return (
    <Autocomplete
      fullWidth
      value={value}
      onChange={onChange}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      options={displayedOptions}
      getOptionLabel={getOptionLabel ? getOptionLabel : undefined}
      loading={loading || isLoading}
      freeSolo={freeSolo}
      isOptionEqualToValue={isOptionEqualToValue || ((option, value) => {
        if (!value) return true;
        return option === value;
      })}
      {...rest}
      renderInput={(params) => (
        <TextField
          {...params}
          onFocus={onFocus}
          label={label}
          error={!!error}
          helperText={helperText || ""}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading || isLoading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          required={required}
        />
      )}
    />
  );
}
